/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState } from 'react';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { AgendaView } from '@features/agenda/utils/agenda-dates';
import type { UserRole } from '@/shared/utils/permissions';
import { canManageEvent } from '@features/agenda/utils/agenda-permissions';
import { isVisitEvent } from '@features/agenda/utils/visit-event';
import { AgendaMobileHeader } from './AgendaMobileHeader';
import { AgendaAddFab } from './AgendaAddFab';
import {
  MobileListView,
  MobileDayView,
  MobileDayAgenda,
  MobileEventPreviewSheet,
  MobileCalendarFilterSheet,
  MobileMonthPickerSheet,
} from './mobile';

interface AgendaMobileViewProps {
  view: AgendaView;
  anchor: Date;
  monthLabel: string;
  events: ExpandedEvent[];
  calendars: Calendar[];
  canCreateEvents: boolean;
  currentUserRole: UserRole;
  userEmail: string;
  onViewChange: (view: AgendaView) => void;
  onNavigate: (direction: 1 | -1) => void;
  onSelectDate: (date: Date) => void;
  onSelectEvent: (event: ExpandedEvent) => void;
  onSlotCreate: (slotDate?: Date) => void;
  onAdd: () => void;
  onEditFromPreview: (event: ExpandedEvent) => void;
  onDeleteFromPreview: (event: ExpandedEvent) => void;
  onOpenSettings?: () => void;
  activeCalendars?: Set<string>;
  onToggleCalendar?: (calendarId: string) => void;
  visitsVisible?: boolean;
  onToggleVisits?: () => void;
  agencyName?: string;
}

/**
 * Full-screen, one-view-at-a-time mobile agenda. Renders the touch-first header
 * (month jumper + chevrons + view switch), the active calendar grid, a
 * slide-up month picker, and a floating add-appointment button above the nav.
 *
 * @param props Agenda state and handlers (mirrors the desktop wiring).
 * @returns Rendered mobile agenda.
 */
export const AgendaMobileView: React.FC<AgendaMobileViewProps> = ({
  view,
  anchor,
  monthLabel,
  events,
  calendars,
  canCreateEvents,
  currentUserRole,
  userEmail,
  onViewChange,
  onNavigate,
  onSelectDate,
  onSelectEvent,
  onSlotCreate,
  onAdd,
  onEditFromPreview,
  onDeleteFromPreview,
  onOpenSettings,
  activeCalendars,
  onToggleCalendar,
  visitsVisible = true,
  onToggleVisits,
  agencyName,
}) => {
  const [monthOpen, setMonthOpen] = useState(false);
  const [previewEvent, setPreviewEvent] = useState<ExpandedEvent | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const handleSelectDate = (date: Date): void => {
    onSelectDate(date);
    setMonthOpen(false);
  };

  const handleDayClick = (date: Date): void => {
    onSelectDate(date);
    onViewChange('day');
  };

  const handleEventSelect = (event: ExpandedEvent): void => {
    if (event.visit_id) {
      onSelectEvent(event);
      return;
    }
    setPreviewEvent(event);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--surface-primary)]">
      <AgendaMobileHeader
        view={view}
        monthLabel={monthLabel}
        onOpenMonth={() => setMonthOpen(true)}
        onNavigate={onNavigate}
        onViewChange={onViewChange}
        onOpenSettings={() => setFilterOpen(true)}
      />

      {/*
        Week and month are desktop grids; the phone offers the day screen and
        the list. A stored preference, a deep link or a wider window can still
        name one of the retired views, so anything that is not the list falls
        to the day screen rather than rendering an empty column.
      */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {view === 'list' && (
          <MobileListView
            anchor={anchor}
            events={events}
            calendars={calendars}
            onSelect={handleEventSelect}
          />
        )}
        {/*
          * The day reads as a list under a week strip, not as a 24-hour
          * timeline. `MobileDayView` still exists and still holds the
          * timeline; the week view is where reading gaps between
          * appointments is the point, and it kept that shape.
          */}
        {view !== 'list' && (
          <MobileDayAgenda
            date={anchor}
            events={events}
            calendars={calendars}
            onSelectDate={onSelectDate}
            onSelect={handleEventSelect}
          />
        )}
      </div>

      {canCreateEvents && <AgendaAddFab onClick={onAdd} />}

      {previewEvent && (
        <MobileEventPreviewSheet
          event={previewEvent}
          calendars={calendars}
          canEdit={!isVisitEvent(previewEvent) && canManageEvent(currentUserRole, previewEvent, userEmail)}
          onEdit={(ev) => { setPreviewEvent(null); onEditFromPreview(ev); }}
          onDelete={(ev) => { setPreviewEvent(null); onDeleteFromPreview(ev); }}
          onClose={() => setPreviewEvent(null)}
        />
      )}

      {filterOpen && activeCalendars && onToggleCalendar && onToggleVisits && (
        <MobileCalendarFilterSheet
          calendars={calendars}
          activeCalendars={activeCalendars}
          onToggleCalendar={onToggleCalendar}
          visitsVisible={visitsVisible}
          onToggleVisits={onToggleVisits}
          onOpenSettings={onOpenSettings ?? (() => {})}
          onClose={() => setFilterOpen(false)}
          agencyName={agencyName}
        />
      )}

      {monthOpen && (
        <MobileMonthPickerSheet
          anchor={anchor}
          onSelectDate={handleSelectDate}
          onClose={() => setMonthOpen(false)}
        />
      )}
    </div>
  );
};
